import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/UI/Header';
import Footer from '../../components/UI/Footer';
import ContactFormSection from './components/ContactFormSection';
import ContactInfoPanel from './components/ContactInfoPanel';
import TestimonialsSection from './components/TestimonialsSection';
import FAQSection from './components/FAQSection';
import Icon from '../../components/AppIcon';
import {
  ContactFormData,
  ContactMethod,
  AvailabilityStatus,
  Testimonial,
  FormSubmissionState
} from
  './types';

import { useContent } from '../../hooks/useContent';
import {
  defaultContactMethods,
  defaultAvailability,
  defaultTestimonials,
  defaultFAQs
} from './constants/defaults';

const ContactForm = () => {
  const [submissionState, setSubmissionState] = useState<FormSubmissionState>({
    isSubmitting: false,
    isSubmitted: false,
    error: null
  });

  // Fetch Dynamic Content
  const { content: contactMethods, loading: loadingMethods } = useContent<ContactMethod[]>('contact_methods', defaultContactMethods);
  const { content: availability, loading: loadingAvailability } = useContent<AvailabilityStatus>('contact_availability', defaultAvailability);
  const { content: testimonials, loading: loadingTestimonials } = useContent<Testimonial[]>('contact_testimonials', defaultTestimonials);
  const { content: faqs, loading: loadingFAQs } = useContent<any[]>('contact_faqs', defaultFAQs);

  const handleFormSubmit = async (formData: ContactFormData) => {
    setSubmissionState({
      isSubmitting: true,
      isSubmitted: false,
      error: null
    });

    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // Mock successful submission
      console.log('Form submitted:', formData);

      setSubmissionState({
        isSubmitting: false,
        isSubmitted: true,
        error: null
      });
    } catch (error) {
      setSubmissionState({
        isSubmitting: false,
        isSubmitted: false,
        error: 'Failed to send message. Please try again or contact me directly.'
      });
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const isLoading = loadingMethods || loadingAvailability || loadingTestimonials || loadingFAQs;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[var(--color-background)] flex items-center justify-center">
        <div className="neubrutal-card bg-white p-10 flex flex-col items-center">
          <div className="w-12 h-12 border-4 border-[#0C2C39]/10 border-t-[#0C2C39] rounded-full animate-spin mb-4"></div>
          <p className="font-black uppercase tracking-widest text-xs text-[#0C2C39]/60">Syncing Uplink...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Contact - Portfolio</title>
        <meta name="description" content="Get in touch for collaboration opportunities, project inquiries, or professional networking. Available for freelance projects, full-time positions, and consultations." />
        <meta name="keywords" content="contact, hire developer, freelance, collaboration, web development, React developer" />
      </Helmet>

      <div className="min-h-screen bg-[var(--color-background)]">
        <Header />

        <main className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16 py-32">
          {/* Hero Section */}
          <div className="mb-24 relative overflow-hidden">
            {/* Subtle Pattern Grid */}
            <div className="absolute inset-x-0 -top-24 h-64 z-0 opacity-[0.03] pointer-events-none"
              style={{
                backgroundImage: 'radial-gradient(#0C2C39 0.5px, transparent 0.5px)',
                backgroundSize: '24px 24px'
              }} />

            <div className="relative z-10">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-white border-2 border-[#0C2C39] rounded-[1.5rem] mb-10 shadow-[4px_4px_0px_0px_#0C2C39] rotate-3">
                <Icon name="MessageSquare" size={32} className="text-[#0C2C39]" />
              </div>

              <h1 className="text-5xl lg:text-7xl font-black text-[#0C2C39] mb-8 tracking-tighter leading-[0.9] font-roboto">
                Initiate <br />
                <span className="text-teal-700/80 italic">Collaboration</span>
              </h1>

              <p className="text-xl text-[#0C2C39]/60 max-w-xl leading-relaxed font-medium">
                Ready to architect your next digital breakthrough? I'm here to facilitate superior software solutions and executive-level collaboration.
              </p>
            </div>
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16 mb-32 relative z-10">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <ContactFormSection
                onSubmit={handleFormSubmit}
                submissionState={submissionState} />
            </div>

            {/* Contact Info Panel */}
            <div className="lg:col-span-1">
              <ContactInfoPanel
                contactMethods={contactMethods}
                availability={availability} />
            </div>
          </div>

          {/* Testimonials Section */}
          <div className="mb-32">
            <TestimonialsSection testimonials={testimonials} />
          </div>

          {/* FAQ Section */}
          <div className="mb-32">
            <FAQSection faqs={faqs} />
          </div>

          {/* Call to Action - Redesigned as an Executive Banner */}
          <div className="neubrutal-card bg-white rounded-[2.5rem] p-12 sm:p-20 text-center relative overflow-hidden group">
            {/* Decorative Background Elements */}
            <div className="absolute -top-12 -right-12 w-48 h-48 bg-teal-700/5 rounded-full blur-3xl group-hover:bg-teal-700/10 transition-colors" />
            <div className="absolute -bottom-12 -left-12 w-48 h-48 bg-[#0C2C39]/5 rounded-full blur-3xl" />

            <div className="relative z-10">
              <h2 className="text-4xl sm:text-5xl font-black text-[#0C2C39] mb-6 tracking-tighter font-roboto">
                Ready for <span className="text-teal-700/80 italic">Deployment?</span>
              </h2>
              <p className="text-lg text-[#0C2C39]/50 mb-12 max-w-2xl mx-auto font-medium">
                Exceptional products begin with precision communication. Let's discuss your architectural objectives and project scalability.
              </p>

              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <a
                  href="mailto:hello@portfolio.dev"
                  className="neubrutal-btn inline-flex items-center justify-center px-10 py-5 bg-[#0C2C39] text-white font-black uppercase tracking-widest text-xs rounded-xl shadow-[4px_4px_0px_0px_rgba(13,148,136,0.3)]">
                  <Icon name="Mail" size={18} className="mr-3" />
                  Dispatch Email
                </a>
                <a
                  href="tel:+15551234567"
                  className="neubrutal-btn inline-flex items-center justify-center px-10 py-5 bg-white text-[#0C2C39] border-2 border-[#0C2C39] font-black uppercase tracking-widest text-xs rounded-xl shadow-[4px_4px_0px_0px_#0C2C39]">
                  <Icon name="Phone" size={18} className="mr-3" />
                  Voice Sync
                </a>
              </div>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>);

};

export default ContactForm;